import React, { useState, useEffect, useCallback } from "react";
import { Search, Loader, RefreshCw, Trash2, Copy, Check, ExternalLink, Image as ImageIcon, Video, Upload, Eye, X } from "lucide-react";
import { mediaAPI, uploadAPI } from "../../services/api";
import { getImageUrl } from "../../utils/image";

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

export default function AdminMedia() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("ALL"); // ALL, IMAGE, VIDEO
  const [uploading, setUploading] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    try {
      const res = await mediaAPI.getAll();
      setMedia(res.data.media || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isVideo = file.type.startsWith("video/") || /\.(mp4|webm|ogg|mov|m4v|mkv)$/i.test(file.name);
    if (isVideo && file.size > 30 * 1024 * 1024) {
      alert(`Video file size cannot exceed 30MB. Selected file is ${(file.size / (1024 * 1024)).toFixed(1)}MB.`);
      e.target.value = "";
      return;
    }

    setUploading(true);
    try {
      if (isVideo) {
        await uploadAPI.video(file, "videos");
      } else {
        await uploadAPI.single(file, "general");
      }
      fetchMedia();
    } catch (err) {
      console.error("Media upload error:", err);
      let msg = "Upload failed";
      if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
        msg = "Upload timed out. The file took too long to transfer. Please check your internet connection.";
      } else if (err.response?.data?.error) {
        msg = err.response.data.error;
      } else if (err.message) {
        msg = err.message;
      }
      alert(`Upload error: ${msg}`);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleCopy = (url) => {
    const fullUrl = window.location.origin + getImageUrl(url);
    navigator.clipboard.writeText(fullUrl);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Delete media file "${filename}"? This may break pages currently referencing it.`)) return;
    try {
      await mediaAPI.delete(filename);
      fetchMedia();
    } catch (err) {
      alert(err.response?.data?.error || "Delete failed");
    }
  };

  const filtered = media.filter(item => {
    const matchesSearch = !search || item.filename.toLowerCase().includes(search.toLowerCase());
    const matchesType = filterType === "ALL" || 
      (filterType === "IMAGE" && item.type === "image") || 
      (filterType === "VIDEO" && item.type === "video");
    return matchesSearch && matchesType;
  });

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-bold text-gray-900 text-xl">Media Library</h2>
          <p className="text-gray-400 text-sm">{media.length} files in central uploads directory</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchMedia} className="w-9 h-9 flex items-center justify-center bg-white border border-gray-200 rounded-xl hover:bg-gray-50 shadow-soft">
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </button>
          <label className={`btn-primary text-xs px-4 py-2.5 cursor-pointer flex items-center gap-1.5 ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
            {uploading ? <Loader className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? "Uploading..." : "Upload Media"}
            <input type="file" className="hidden" accept="image/*,video/mp4,video/webm,video/ogg,video/quicktime" onChange={handleFileUpload} disabled={uploading} />
          </label>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex gap-2">
          {["ALL", "IMAGE", "VIDEO"].map(t => (
            <button
              key={t}
              onClick={() => setFilterType(t)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${
                filterType === t ? "bg-brand-primary text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {t === "ALL" ? "All Files" : t === "IMAGE" ? "Images" : "Videos"} (
              {t === "ALL" ? media.length : media.filter(m => m.type.toUpperCase() === t).length}
              )
            </button>
          ))}
        </div>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            className="input-field pl-9 py-2 text-sm"
            placeholder="Search filenames..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 bg-white rounded-2xl shadow-card">
          <Loader className="w-6 h-6 text-brand-primary animate-spin mr-2" />
          <span className="text-gray-400 text-sm">Loading media library...</span>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filtered.map(item => (
            <div key={item.filename} className="bg-white rounded-2xl border border-gray-100 shadow-soft overflow-hidden group hover:shadow-hover transition-all flex flex-col justify-between">
              <div
                className="h-36 bg-gray-50 relative flex items-center justify-center cursor-pointer overflow-hidden"
                onClick={() => setPreviewItem(item)}
              >
                {item.type === "image" ? (
                  <img src={getImageUrl(item.url)} alt={item.filename} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={e => { e.target.style.display = "none"; }} />
                ) : (
                  <div className="flex flex-col items-center justify-center text-gray-400 group-hover:text-brand-primary transition-colors">
                    <Video className="w-10 h-10 mb-1" />
                    <span className="text-[11px] font-semibold uppercase">Video</span>
                  </div>
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <span className="p-2 bg-white/90 rounded-xl text-gray-700 hover:text-brand-primary transition-colors">
                    <Eye className="w-4 h-4" />
                  </span>
                </div>
              </div>

              <div className="p-3">
                <p className="text-xs font-semibold text-gray-800 truncate mb-1" title={item.filename}>{item.filename}</p>
                <div className="flex items-center justify-between text-[11px] text-gray-400 mb-2">
                  <span>{formatBytes(item.size)}</span>
                  <span className="uppercase">{item.extension}</span>
                </div>
                <div className="flex gap-1 pt-2 border-t border-gray-100">
                  <button
                    onClick={() => handleCopy(item.url)}
                    className="flex-1 py-1.5 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-600 text-[11px] font-medium flex items-center justify-center gap-1 transition-colors"
                    title="Copy URL"
                  >
                    {copiedUrl === item.url ? (
                      <><Check className="w-3 h-3 text-green-600" /><span className="text-green-600 font-semibold">Copied</span></>
                    ) : (
                      <><Copy className="w-3 h-3" /><span>Copy</span></>
                    )}
                  </button>
                  <button
                    onClick={() => handleDelete(item.filename)}
                    className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {filtered.length === 0 && (
            <div className="col-span-full text-center py-16 bg-white rounded-2xl shadow-card text-gray-400">
              <ImageIcon className="w-12 h-12 mx-auto mb-3 text-gray-200" />
              No media files found
            </div>
          )}
        </div>
      )}

      {/* Preview Modal */}
      {previewItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden animate-fadeIn">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <div className="truncate pr-4">
                <h3 className="font-heading font-bold text-gray-900 text-sm truncate">{previewItem.filename}</h3>
                <span className="text-xs text-gray-400">{formatBytes(previewItem.size)} · {previewItem.extension.toUpperCase()}</span>
              </div>
              <button onClick={() => setPreviewItem(null)} className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 bg-gray-900 flex items-center justify-center max-h-[60vh] overflow-hidden">
              {previewItem.type === "image" ? (
                <img src={getImageUrl(previewItem.url)} alt={previewItem.filename} className="max-h-full max-w-full object-contain rounded-lg" />
              ) : (
                <video src={getImageUrl(previewItem.url)} controls className="max-h-full max-w-full rounded-lg" />
              )}
            </div>
            <div className="p-4 bg-gray-50 flex items-center justify-between gap-3">
              <input
                className="input-field text-xs flex-1 bg-white font-mono"
                readOnly
                value={window.location.origin + getImageUrl(previewItem.url)}
              />
              <button
                onClick={() => handleCopy(previewItem.url)}
                className="btn-primary text-xs px-4 py-2 flex items-center gap-1.5"
              >
                {copiedUrl === previewItem.url ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copiedUrl === previewItem.url ? "Copied" : "Copy URL"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
